"use client";

import { m } from "framer-motion";
import { useEffect, useRef, useState } from "react";

interface MarqueeProps {
  children: React.ReactNode;
  speed?: number;
  direction?: "left" | "right";
  className?: string;
  pauseOnHover?: boolean;
}

const GAP_PX = 48; // matches gap-12

export default function Marquee({
  children,
  speed = 30,
  direction = "left",
  className = "",
  pauseOnHover = true,
}: MarqueeProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  // Enough copies that, at any point of the loop, the visible track is at
  // least one copy longer than the container — otherwise very wide viewports
  // show an empty tail. Starts at 2 (SSR) and grows after measuring.
  const [copies, setCopies] = useState(2);

  useEffect(() => {
    const outer = outerRef.current;
    const copy = copyRef.current;
    if (!outer || !copy) return;

    const measure = () => {
      const containerW = outer.clientWidth;
      const copyW = copy.getBoundingClientRect().width + GAP_PX;
      if (!containerW || !copyW) return;
      setCopies(Math.max(2, Math.ceil(containerW / copyW) + 1));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(outer);
    ro.observe(copy);
    return () => ro.disconnect();
  }, []);

  const shift = `-${100 / copies}%`;

  return (
    <div
      ref={outerRef}
      className={`overflow-hidden ${className}`}
      style={{ maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)" }}
    >
      <m.div
        key={copies}
        className={`flex gap-12 items-center w-max ${
          pauseOnHover ? "hover:[animation-play-state:paused]" : ""
        }`}
        animate={{
          x: direction === "left" ? ["0%", shift] : [shift, "0%"],
        }}
        transition={{
          duration: speed,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        {Array.from({ length: copies }, (_, i) => (
          <div
            key={i}
            ref={i === 0 ? copyRef : undefined}
            className="flex gap-12 items-center shrink-0"
          >
            {children}
          </div>
        ))}
      </m.div>
    </div>
  );
}
